<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import WootLabel from 'dashboard/components-next/label/Label.vue';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';
import { RECONCILIATION_STATE_OPTIONS } from './utils/automationOperationsConstants';

defineProps({
  replayResultStatus: { type: String, default: '' },
  canLoadExecution: { type: Boolean, default: false },
  canPreviewReplay: { type: Boolean, default: false },
  canRunReplay: { type: Boolean, default: false },
  canReviewApproval: { type: Boolean, default: false },
  canReconcile: { type: Boolean, default: false },
  isExecutionLoading: { type: Boolean, default: false },
  isReplayPlanLoading: { type: Boolean, default: false },
  isReplayLoading: { type: Boolean, default: false },
  isApprovalLoading: { type: Boolean, default: false },
  isReconciliationLoading: { type: Boolean, default: false },
});

defineEmits([
  'queryChange',
  'loadExecution',
  'planReplay',
  'runReplay',
  'approve',
  'reject',
  'reconcile',
]);

const form = defineModel('form', {
  type: Object,
  required: true,
});

const { formatStatus, labelColor } = useAutomationOperationsFormatters();
</script>

<template>
  <div class="mb-3 flex items-center justify-between gap-3">
    <span class="text-heading-3 text-n-slate-12">
      {{ $t('AUTOMATION.OPERATIONS.REPLAY.TITLE') }}
    </span>
    <WootLabel
      v-if="replayResultStatus"
      :label="formatStatus(replayResultStatus)"
      :color="labelColor(replayResultStatus)"
      compact
    />
  </div>
  <div
    class="grid gap-3 md:grid-cols-2 xl:grid-cols-[7rem_9rem_minmax(12rem,1fr)_7rem_minmax(10rem,0.8fr)_7rem_minmax(10rem,0.8fr)_auto] xl:items-end"
  >
    <label class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.EXECUTION_ID') }}
      </span>
      <input
        v-model="form.executionId"
        data-test="automation-execution-id"
        type="number"
        min="1"
        class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
        @change="$emit('queryChange')"
      />
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.ACTION_REF') }}
      </span>
      <input
        v-model="form.actionRef"
        data-test="automation-action-ref"
        type="text"
        class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
        @change="$emit('queryChange')"
      />
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.REASON') }}
      </span>
      <input
        v-model="form.reason"
        data-test="automation-operation-reason"
        type="text"
        minlength="8"
        class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
      />
    </label>
    <label class="flex h-8 items-center gap-2">
      <input
        v-model="form.force"
        type="checkbox"
        class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary-50"
      />
      <span class="text-body-main text-n-slate-12">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.FORCE') }}
      </span>
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.NEW_KEY') }}
      </span>
      <input
        v-model="form.newIdempotencyKey"
        data-test="automation-new-idempotency-key"
        type="text"
        class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
      />
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.RECONCILE_STATE') }}
      </span>
      <select
        v-model="form.reconciliationState"
        data-test="automation-reconciliation-state"
        class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
      >
        <option
          v-for="option in RECONCILIATION_STATE_OPTIONS"
          :key="option"
          :value="option"
        >
          {{ option }}
        </option>
      </select>
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.OPERATIONS.REPLAY.RECONCILE_KEY') }}
      </span>
      <input
        v-model="form.reconciliationKey"
        data-test="automation-reconciliation-key"
        type="text"
        class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
      />
    </label>
    <div class="flex flex-wrap gap-2">
      <Button
        :label="$t('AUTOMATION.OPERATIONS.EXECUTION.DETAIL')"
        icon="i-lucide-list-tree"
        color="slate"
        variant="outline"
        size="sm"
        :is-loading="isExecutionLoading"
        :disabled="!canLoadExecution"
        @click="$emit('loadExecution')"
      />
      <Button
        :label="$t('AUTOMATION.OPERATIONS.REPLAY.PLAN')"
        icon="i-lucide-search-check"
        color="slate"
        variant="outline"
        size="sm"
        :is-loading="isReplayPlanLoading"
        :disabled="!canPreviewReplay"
        @click="$emit('planReplay')"
      />
      <Button
        :label="$t('AUTOMATION.OPERATIONS.REPLAY.RUN')"
        data-test="automation-replay-run"
        icon="i-lucide-play"
        color="ruby"
        variant="outline"
        size="sm"
        :is-loading="isReplayLoading"
        :disabled="!canRunReplay"
        @click="$emit('runReplay')"
      />
      <Button
        :label="$t('AUTOMATION.OPERATIONS.REPLAY.APPROVE')"
        icon="i-lucide-check"
        color="teal"
        variant="outline"
        size="sm"
        :is-loading="isApprovalLoading"
        :disabled="!canReviewApproval"
        @click="$emit('approve')"
      />
      <Button
        :label="$t('AUTOMATION.OPERATIONS.REPLAY.REJECT')"
        data-test="automation-replay-reject"
        icon="i-lucide-x"
        color="ruby"
        variant="outline"
        size="sm"
        :is-loading="isApprovalLoading"
        :disabled="!canReviewApproval"
        @click="$emit('reject')"
      />
      <Button
        :label="$t('AUTOMATION.OPERATIONS.REPLAY.RECONCILE')"
        data-test="automation-reconcile-unknown"
        icon="i-lucide-file-check-2"
        color="blue"
        variant="outline"
        size="sm"
        :is-loading="isReconciliationLoading"
        :disabled="!canReconcile"
        @click="$emit('reconcile')"
      />
    </div>
  </div>
</template>
